import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, RefreshCcw } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const importFormSchema = z.object({
  name: z.string().min(3, {
    message: "Project Name must be at least 3 characters.",
  }),
  domainName: z
    .string()
    .min(3, {
      message: "Domain Name must be at least 3 characters.",
    })
    .refine(
      (value) => {
        if (/^https?:\/\//i.test(value)) return false;
        const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        return domainRegex.test(value);
      },
      {
        message: "Please enter a valid domain name (e.g., myapp.com). Do not include http:// or https://",
      }
    ),
  selectedRepos: z.array(z.object({
    id: z.number(),
    name: z.string(),
    full_name: z.string(),
    private: z.boolean(),
    html_url: z.string(),
    description: z.string().nullable().optional(),
    language: z.string().nullable().optional(),
  })).min(1, {
    message: "Please select at least one repository.",
  }),
});

interface Repo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  description: string | null;
  language: string | null;
  updated_at: string;
}

export default function ImportProject() {
  const navigate = useNavigate();
  const [repos, setRepos] = React.useState<Repo[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<z.infer<typeof importFormSchema>>({
    resolver: zodResolver(importFormSchema),
    defaultValues: {
      name: "",
      domainName: "",
      selectedRepos: [],
    },
  });

  const fetchRepos = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/api/github/repos`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setRepos(data.repos);
      } else {
        const err = await res.json();
        setError(err.error || "Failed to fetch repositories.");
      }
    } catch (e) {
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchRepos();
  }, []);

  const onSubmit = (values: z.infer<typeof importFormSchema>) => {
    const importedApps = values.selectedRepos.map((repo) => ({
      id: `${values.name}-${repo.name}-${crypto.randomUUID().slice(0, 8)}`,
      type: "github-repo",
      name: repo.name,
      template: "custom",
      config: {
        repoUrl: repo.html_url,
        private: repo.private,
        language: repo.language,
        description: repo.description,
        client: false,
        port: "3000",
      },
    }));

    navigate("/new", {
      state: {
        importedProject: {
          name: values.name,
          domainName: values.domainName,
          apps: importedApps,
        },
      },
    });
  };

  const selectedRepos = form.watch("selectedRepos");

  const toggleRepo = React.useCallback((repo: Repo) => {
    const current = form.getValues("selectedRepos");
    const exists = current.find((r) => r.id === repo.id);
    if (exists) {
      form.setValue(
        "selectedRepos",
        current.filter((r) => r.id !== repo.id)
      );
    } else {
      form.setValue("selectedRepos", [...current, repo]);
    }
  }, [form]);

  return (
    <div className="container mx-auto max-w-3xl py-10">
      <div className="mb-8 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Import from GitHub</h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
              <CardDescription>
                Enter the basic details for your new project.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Name</FormLabel>
                    <FormControl>
                      <Input placeholder="My Awesome Project" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="domainName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Domain Name</FormLabel>
                    <FormControl>
                      <Input placeholder="myapp.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      The domain where your project will be hosted.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="space-y-1.5">
                <CardTitle>Select Repositories</CardTitle>
                <CardDescription>
                  Choose the repositories you want to import into your project.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  fetchRepos();
                }}
                disabled={loading}
              >
               <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
               Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {error ? (
                <Alert variant="destructive">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : loading ? (
                 <div className="flex h-40 items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                 </div>
              ) : repos.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  No repositories found.
                </div>
              ) : (
                <div className="h-[400px] pr-4 overflow-y-auto">
                   <div className="space-y-2">
                     {repos.map((repo) => {
                       const isSelected = selectedRepos.some(r => r.id === repo.id);
                       return (
                         <div
                           key={repo.id}
                           className={`flex items-start space-x-3 rounded-md border p-3 transition-colors cursor-pointer ${
                             isSelected ? "bg-accent" : "hover:bg-muted/50"
                           }`}
                           onClick={() => toggleRepo(repo)}
                         >
                           <Checkbox
                             checked={isSelected}
                             className="pointer-events-none"
                           />
                           <div className="space-y-1 leading-none w-full">
                             <div className="flex items-center justify-between">
                               <FormLabel className="font-semibold cursor-pointer pointer-events-none">
                                 {repo.name}
                               </FormLabel>
                               {repo.private && (
                                 <Badge variant="secondary" className="text-xs">Private</Badge>
                               )}
                             </div>
                             <FormDescription className="cursor-pointer">
                               {repo.description || "No description"}
                             </FormDescription>
                             {repo.language && (
                                <Badge variant="outline" className="mt-2 text-xs font-normal">
                                  {repo.language}
                                </Badge>
                             )}
                           </div>
                         </div>
                       );
                     })}
                   </div>
                </div>
              )}
               <FormMessage>
                  {form.formState.errors.selectedRepos?.message}
               </FormMessage>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={loading}>
              Import & Configure Nodes <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
